// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Trophy, Upload, X } from 'lucide-react';

interface FeaturedPlayer {
  id: string;
  name: string;
  state: string | null;
  class_year: string | null;
  position: string | null;
  height: string | null;
  photo_url: string | null;
  video_url: string | null;
  twitter_url: string | null;
  instagram_url: string | null;
  evaluation_notes: string | null;
  display_order: number;
  is_active: boolean;
}

interface ConfirmedCoach {
  id: string;
  coach_name: string;
  college_name: string;
  division: string | null;
  title: string | null;
  photo_url: string | null;
  is_confirmed: boolean;
  display_order: number;
}

const MayEventAdmin: React.FC = () => {
  const [players, setPlayers] = useState<FeaturedPlayer[]>([]);
  const [coaches, setCoaches] = useState<ConfirmedCoach[]>([]);
  const [loading, setLoading] = useState(true);
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [coachDialogOpen, setCoachDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<FeaturedPlayer | null>(null);
  const [editingCoach, setEditingCoach] = useState<ConfirmedCoach | null>(null);
  const [uploadingPlayerImage, setUploadingPlayerImage] = useState(false);
  const [uploadingCoachImage, setUploadingCoachImage] = useState(false);
  
  const playerFileInputRef = useRef<HTMLInputElement>(null);
  const coachFileInputRef = useRef<HTMLInputElement>(null);

  const [playerForm, setPlayerForm] = useState({
    name: '',
    state: '',
    class_year: '',
    position: '',
    height: '',
    photo_url: '',
    video_url: '',
    twitter_url: '',
    instagram_url: '',
    evaluation_notes: '',
  });

  const [coachForm, setCoachForm] = useState({
    coach_name: '',
    college_name: '',
    division: '',
    title: '',
    photo_url: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [playersRes, coachesRes] = await Promise.all([
        supabase.from('featured_may_players').select('*').order('display_order', { ascending: true }),
        supabase.from('may_event_confirmed_coaches').select('*').order('display_order', { ascending: true }),
      ]);
      if (playersRes.data) setPlayers(playersRes.data);
      if (coachesRes.data) setCoaches(coachesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file: File, type: 'player' | 'coach'): Promise<string | null> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${type}-${Date.now()}.${fileExt}`;
    const filePath = `${type}s/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('may-event-images')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      toast.error('Failed to upload image');
      return null;
    }

    const { data } = supabase.storage
      .from('may-event-images')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handlePlayerImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPlayerImage(true);
    const url = await uploadImage(file, 'player');
    if (url) {
      setPlayerForm({ ...playerForm, photo_url: url });
      toast.success('Image uploaded');
    }
    setUploadingPlayerImage(false);
    if (playerFileInputRef.current) playerFileInputRef.current.value = '';
  };

  const handleCoachImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingCoachImage(true);
    const url = await uploadImage(file, 'coach');
    if (url) {
      setCoachForm({ ...coachForm, photo_url: url });
      toast.success('Image uploaded');
    }
    setUploadingCoachImage(false);
    if (coachFileInputRef.current) coachFileInputRef.current.value = '';
  };

  const openPlayerDialog = (player?: FeaturedPlayer) => {
    if (player) {
      setEditingPlayer(player);
      setPlayerForm({
        name: player.name || '',
        state: player.state || '',
        class_year: player.class_year || '',
        position: player.position || '',
        height: player.height || '',
        photo_url: player.photo_url || '',
        video_url: player.video_url || '',
        twitter_url: player.twitter_url || '',
        instagram_url: player.instagram_url || '',
        evaluation_notes: player.evaluation_notes || '',
      });
    } else {
      setEditingPlayer(null);
      setPlayerForm({ name: '', state: '', class_year: '', position: '', height: '', photo_url: '', video_url: '', twitter_url: '', instagram_url: '', evaluation_notes: '' });
    }
    setPlayerDialogOpen(true);
  };

  const savePlayer = async () => {
    if (!playerForm.name.trim()) {
      toast.error('Player name is required');
      return;
    }
    try {
      if (editingPlayer) {
        const { error } = await supabase.from('featured_may_players').update({ ...playerForm, updated_at: new Date().toISOString() }).eq('id', editingPlayer.id);
        if (error) throw error;
        toast.success('Player updated');
      } else {
        const { error } = await supabase.from('featured_may_players').insert({ ...playerForm, is_active: true, display_order: players.length });
        if (error) throw error;
        toast.success('Player added');
      }
      setPlayerDialogOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save player');
    }
  };

  const deletePlayer = async (id: string) => {
    if (!confirm('Delete this player?')) return;
    try {
      const { error } = await supabase.from('featured_may_players').delete().eq('id', id);
      if (error) throw error;
      toast.success('Player deleted');
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete');
    }
  };

  const togglePlayerActive = async (player: FeaturedPlayer) => {
    try {
      const { error } = await supabase.from('featured_may_players').update({ is_active: !player.is_active }).eq('id', player.id);
      if (error) throw error;
      toast.success(player.is_active ? 'Player hidden' : 'Player shown');
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update');
    }
  };

  const openCoachDialog = (coach?: ConfirmedCoach) => {
    if (coach) {
      setEditingCoach(coach);
      setCoachForm({ coach_name: coach.coach_name || '', college_name: coach.college_name || '', division: coach.division || '', title: coach.title || '', photo_url: coach.photo_url || '' });
    } else {
      setEditingCoach(null);
      setCoachForm({ coach_name: '', college_name: '', division: '', title: '', photo_url: '' });
    }
    setCoachDialogOpen(true);
  };

  const saveCoach = async () => {
    if (!coachForm.coach_name.trim() || !coachForm.college_name.trim()) {
      toast.error('Coach name and college are required');
      return;
    }
    try {
      if (editingCoach) {
        const { error } = await supabase.from('may_event_confirmed_coaches').update({ ...coachForm, updated_at: new Date().toISOString() }).eq('id', editingCoach.id);
        if (error) throw error;
        toast.success('Coach updated');
      } else {
        const { error } = await supabase.from('may_event_confirmed_coaches').insert({ ...coachForm, is_confirmed: true, display_order: coaches.length });
        if (error) throw error;
        toast.success('Coach added');
      }
      setCoachDialogOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save coach');
    }
  };

  const deleteCoach = async (id: string) => {
    if (!confirm('Delete this coach?')) return;
    try {
      const { error } = await supabase.from('may_event_confirmed_coaches').delete().eq('id', id);
      if (error) throw error;
      toast.success('Coach deleted');
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">May Live Period KC Admin</h1>
        <p className="text-muted-foreground">Manage featured players and confirmed college coach attendees</p>
      </div>

      <Tabs defaultValue="players" className="space-y-4">
        <TabsList>
          <TabsTrigger value="players" className="gap-2">
            <Trophy className="h-4 w-4" />
            Featured Players
          </TabsTrigger>
          <TabsTrigger value="coaches" className="gap-2">
            <Users className="h-4 w-4" />
            Confirmed Coaches
          </TabsTrigger>
        </TabsList>

        <TabsContent value="players">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Featured Players ({players.length})</CardTitle>
              <Button onClick={() => openPlayerDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Player
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Photo</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Height</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {players.map((player) => (
                    <TableRow key={player.id}>
                      <TableCell>
                        {player.photo_url ? (
                          <img src={player.photo_url} alt={player.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs">N/A</div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{player.name}</TableCell>
                      <TableCell>{player.state || '-'}</TableCell>
                      <TableCell>{player.class_year || '-'}</TableCell>
                      <TableCell>{player.position || '-'}</TableCell>
                      <TableCell>{player.height || '-'}</TableCell>
                      <TableCell>
                        <Button variant={player.is_active ? 'default' : 'outline'} size="sm" onClick={() => togglePlayerActive(player)}>
                          {player.is_active ? 'Active' : 'Hidden'}
                        </Button>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => openPlayerDialog(player)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deletePlayer(player.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {players.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">No featured players yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="coaches">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Confirmed College Coaches ({coaches.length})</CardTitle>
              <Button onClick={() => openCoachDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Coach
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Photo</TableHead>
                    <TableHead>Coach Name</TableHead>
                    <TableHead>College</TableHead>
                    <TableHead>Division</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coaches.map((coach) => (
                    <TableRow key={coach.id}>
                      <TableCell>
                        {coach.photo_url ? (
                          <img src={coach.photo_url} alt={coach.coach_name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs">N/A</div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{coach.coach_name}</TableCell>
                      <TableCell>{coach.college_name}</TableCell>
                      <TableCell>{coach.division || '-'}</TableCell>
                      <TableCell>{coach.title || '-'}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => openCoachDialog(coach)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteCoach(coach.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {coaches.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">No confirmed coaches yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Player Dialog */}
      <Dialog open={playerDialogOpen} onOpenChange={setPlayerDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlayer ? 'Edit Player' : 'Add Featured Player'}</DialogTitle>
            <DialogDescription>Fill in the player details below. Upload a photo or enter a URL.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input id="name" value={playerForm.name} onChange={(e) => setPlayerForm({ ...playerForm, name: e.target.value })} placeholder="Player name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Input id="state" value={playerForm.state} onChange={(e) => setPlayerForm({ ...playerForm, state: e.target.value })} placeholder="e.g. TX, CA" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="class_year">Class/Grad Year</Label>
              <Input id="class_year" value={playerForm.class_year} onChange={(e) => setPlayerForm({ ...playerForm, class_year: e.target.value })} placeholder="e.g. 2026" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <Input id="position" value={playerForm.position} onChange={(e) => setPlayerForm({ ...playerForm, position: e.target.value })} placeholder="e.g. PG, SG" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="height">Height</Label>
              <Input id="height" value={playerForm.height} onChange={(e) => setPlayerForm({ ...playerForm, height: e.target.value })} placeholder="e.g. 6-2" />
            </div>
            <div className="space-y-2">
              <Label>Photo</Label>
              <div className="flex gap-2">
                <input type="file" ref={playerFileInputRef} accept="image/*" onChange={handlePlayerImageUpload} className="hidden" />
                <Button type="button" variant="outline" size="sm" onClick={() => playerFileInputRef.current?.click()} disabled={uploadingPlayerImage}>
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingPlayerImage ? 'Uploading...' : 'Upload'}
                </Button>
                {playerForm.photo_url && (
                  <div className="flex items-center gap-2">
                    <img src={playerForm.photo_url} alt="Preview" className="w-8 h-8 rounded object-cover" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => setPlayerForm({ ...playerForm, photo_url: '' })}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              <Input id="photo_url" value={playerForm.photo_url} onChange={(e) => setPlayerForm({ ...playerForm, photo_url: e.target.value })} placeholder="Or paste image URL" className="mt-2" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="video_url">Video URL</Label>
              <Input id="video_url" value={playerForm.video_url} onChange={(e) => setPlayerForm({ ...playerForm, video_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="twitter_url">Twitter URL</Label>
              <Input id="twitter_url" value={playerForm.twitter_url} onChange={(e) => setPlayerForm({ ...playerForm, twitter_url: e.target.value })} placeholder="https://twitter.com/..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instagram_url">Instagram URL</Label>
              <Input id="instagram_url" value={playerForm.instagram_url} onChange={(e) => setPlayerForm({ ...playerForm, instagram_url: e.target.value })} placeholder="https://instagram.com/..." />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="evaluation_notes">Evaluation Notes</Label>
              <Input id="evaluation_notes" value={playerForm.evaluation_notes} onChange={(e) => setPlayerForm({ ...playerForm, evaluation_notes: e.target.value })} placeholder="Brief scouting notes..." />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPlayerDialogOpen(false)}>Cancel</Button>
            <Button onClick={savePlayer}>{editingPlayer ? 'Update' : 'Add'} Player</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Coach Dialog */}
      <Dialog open={coachDialogOpen} onOpenChange={setCoachDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCoach ? 'Edit Coach' : 'Add Confirmed Coach'}</DialogTitle>
            <DialogDescription>Fill in the coach details below.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="coach_name">Coach Name *</Label>
              <Input id="coach_name" value={coachForm.coach_name} onChange={(e) => setCoachForm({ ...coachForm, coach_name: e.target.value })} placeholder="Coach name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="college_name">College/University *</Label>
              <Input id="college_name" value={coachForm.college_name} onChange={(e) => setCoachForm({ ...coachForm, college_name: e.target.value })} placeholder="University name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="division">Division</Label>
              <Select value={coachForm.division} onValueChange={(value) => setCoachForm({ ...coachForm, division: value })}>
                <SelectTrigger><SelectValue placeholder="Select division" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="D1">Division I</SelectItem>
                  <SelectItem value="D2">Division II</SelectItem>
                  <SelectItem value="D3">Division III</SelectItem>
                  <SelectItem value="NAIA">NAIA</SelectItem>
                  <SelectItem value="JUCO">JUCO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" value={coachForm.title} onChange={(e) => setCoachForm({ ...coachForm, title: e.target.value })} placeholder="e.g. Head Coach, Assistant" />
            </div>
            <div className="space-y-2">
              <Label>Photo</Label>
              <div className="flex gap-2">
                <input type="file" ref={coachFileInputRef} accept="image/*" onChange={handleCoachImageUpload} className="hidden" />
                <Button type="button" variant="outline" size="sm" onClick={() => coachFileInputRef.current?.click()} disabled={uploadingCoachImage}>
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingCoachImage ? 'Uploading...' : 'Upload'}
                </Button>
                {coachForm.photo_url && (
                  <div className="flex items-center gap-2">
                    <img src={coachForm.photo_url} alt="Preview" className="w-8 h-8 rounded object-cover" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => setCoachForm({ ...coachForm, photo_url: '' })}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              <Input id="coach_photo_url" value={coachForm.photo_url} onChange={(e) => setCoachForm({ ...coachForm, photo_url: e.target.value })} placeholder="Or paste image URL" className="mt-2" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCoachDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveCoach}>{editingCoach ? 'Update' : 'Add'} Coach</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MayEventAdmin;
